import { LitElement, html, css } from 'lit-element';
import memoize from 'lodash.memoize';
import { connect } from '../core/connect';
import {
  fetchOffer,
  productHasChangedComponents,
  fetchOrders,
  setPreviewStandardOffer,
  setPreviewUpsellOffer
} from '../core/actions';
import { isSameProduct, configSelector } from '../core/selectors';
import { product as productProp, defaultFrequency as defaultFrequencyProp, auth as authProp } from '../core/props';

const memoizeKey = (...args) => JSON.stringify(args);

export const productAndComponents = memoize(
  (product, components) => Object.assign({ components }, product),
  memoizeKey
);

export class Offer extends LitElement {
  static get properties() {
    return {
      ...super.properties,
      product: productProp,
      defaultFrequency: defaultFrequencyProp,
      productComponents: { type: Array, attribute: 'product-components' },
      type: { type: String },
      showTooltip: { type: Boolean },
      offerId: { type: String, attribute: false },
      inStock: { type: Boolean, attribute: false },
      eligible: { type: Boolean, attribute: false },
      hasUpcomingOrder: { type: Boolean, attribute: false },
      auth: authProp,
      loading: { type: Boolean, attribute: false },
      previewStandardOffer: { type: Boolean, attribute: 'preview-standard-offer' },
      previewUpsellOffer: { type: Boolean, attribute: 'preview-upsell-offer' }
    };
  }

  static get styles() {
    return css`
      :host[hidden] {
        display: none;
      }
      :host {
        display: block;
      }
      :host {
        color: var(--og-global-font-color, #000);
        font-size: var(--og-global-font-size, inherit);
        font-family: var(--og-global-font-family, inherit);
      }
      p {
        margin: 0 0 0.3em;
      }
    `;
  }

  constructor() {
    super();
    this.module = 'pdp';
    this.product = {};
    this.fetchOffer = () => 0;
    this.fetchOrders = () => 0;
    this.setPreviewStandardOffer = () => 0;
    this.setPreviewUpsellOffer = () => 0;
    this.productHasChangedComponents = () => 0;
  }

  updated(changed) {
    if (changed.has('previewStandardOffer')) {
      this.setPreviewStandardOffer(this.previewStandardOffer);
    }

    if (changed.has('previewUpsellOffer')) {
      this.setPreviewUpsellOffer(this.previewUpsellOffer);
    }

    if (changed.has('product') && this.product.id) {
      if (this.isPreview) {
        this.offerId = 'preview-offer-id';
        this.inStock = true;
        this.eligible = true;
      } else {
        this.fetchOffer(this.product.id);
      }
    }

    if (changed.has('auth') && this.auth && !this.isPreview) {
      this.fetchOrders();
    }

    if (changed.has('productComponents')) {
      const newProductWithComponents = productAndComponents(this.product, this.productComponents);
      const oldProductWithComponents = Object.assign({}, this.product, {
        components: changed.get('productComponents')
      });

      if (!isSameProduct(newProductWithComponents, oldProductWithComponents)) {
        this.productHasChangedComponents(newProductWithComponents, oldProductWithComponents);
      }
    }
  }

  get iuEligible() {
    return this.auth && this.inStock && this.hasUpcomingOrder;
  }

  get subscriptionEligible() {
    return this.inStock && this.eligible;
  }

  get isPreview() {
    return this.previewStandardOffer || this.previewUpsellOffer;
  }

  emptyTemplate() {
    return html`
      <slot name="loading-template"></slot>
    `;
  }

  iuTemplate() {
    return html`
      <slot name="iu-template">
        <div class="og-iu-offer">
          <og-upsell-button></og-upsell-button>
          <og-upsell-modal></og-upsell-modal>
        </div>
      </slot>
    `;
  }

  selectTemplate() {
    return html`
      <div class="og-offer">
        <og-text key="offerEveryLabel"></og-text>
        <og-optin-select></og-optin-select>
        ${this.showTooltip ? this.tooltipTemplate() : this.emptyTemplate()}
      </div>
    `;
  }

  toggleTemplate() {
    return html`
      <div class="og-offer">
        <p>
          <og-optin-toggle></og-optin-toggle>
          ${this.showTooltip ? this.tooltipTemplate() : this.emptyTemplate()}
        </p>
        <p style="margin-left: 1.5em">
          <og-text key="offerEveryLabel"></og-text>
          <og-select-frequency></og-select-frequency>
        </p>
      </div>
    `;
  }

  tooltipTemplate() {
    return html`
      <og-tooltip>
        <div slot="trigger">
          <og-text key="offerTooltipTrigger"></og-text>
        </div>
        <div slot="content">
          <og-text key="offerTooltipContent"></og-text>
        </div>
      </og-tooltip>
    `;
  }

  radioTemplate() {
    return html`
      <div class="og-offer">
        <p>
          <og-optout-button></og-optout-button>
        </p>
        <p>
          <og-optin-button></og-optin-button>
          ${this.showTooltip ? this.tooltipTemplate() : this.emptyTemplate()}
        </p>
        <p style="margin-left: 1.5em">
          <og-text key="offerEveryLabel"></og-text>
          <og-select-frequency></og-select-frequency>
        </p>
      </div>
    `;
  }

  standardOfferTemplate() {
    switch (this.type) {
      case 'select':
        return this.selectTemplate();
      case 'toggle':
        return this.toggleTemplate();
      case 'radio':
      default:
        return this.radioTemplate();
    }
  }

  render() {
    if (this.isPreview) {
      return html`
        <div class="og-offer-preview">
          ${this.previewStandardOffer
            ? html`
                <div class="og-standard-offer-preview">${this.standardOfferTemplate()}</div>
              `
            : this.emptyTemplate()}
          ${this.previewUpsellOffer
            ? html`
                <div class="og-upsell-offer-preview">${this.iuTemplate()}</div>
              `
            : this.emptyTemplate()}
        </div>
      `;
    }

    if (this.loading) return this.emptyTemplate();
    if (this.iuEligible) return this.iuTemplate();
    if (this.subscriptionEligible)
      return html`
        <slot name="standard-template">${this.standardOfferTemplate()}</slot>
      `;

    return this.emptyTemplate();
  }
}

export const mapStateToProps = (state, ownProps) => ({
  ...configSelector(state, ownProps, 'defaultFrequency'),
  type: state.config && state.config.offerType ? state.config.offerType : 'radio',
  showTooltip: state.config && typeof state.config.showTooltip === 'boolean' ? state.config.showTooltip : true,
  auth: state.auth,
  loading: state.loading,
  offerId: ((state.productOffer || {})[(ownProps.product || {}).id] || [])[0],
  inStock: (state.inStock || {})[(ownProps.product || {}).id] || false,
  eligible: (state.autoshipEligible || {})[(ownProps.product || {}).id] || false,
  hasUpcomingOrder: !!(state.nextUpcomingOrder && state.nextUpcomingOrder.public_id)
});

export const ConnectedOffer = connect(
  mapStateToProps,
  {
    fetchOffer,
    fetchOrders,
    productHasChangedComponents,
    setPreviewStandardOffer,
    setPreviewUpsellOffer
  }
)(Offer);

export default ConnectedOffer;
